import classNames from 'classnames';
import styles from 'styles/components/common/elements/Dropdown.module.scss';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron_up.svg';
import { ReactComponent as ChevronDown } from 'assets/icons/chevron_down.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { motion, AnimatePresence } from 'framer-motion';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import useCombineRef from 'hooks/useCombineRef';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TDropdownItem = { label: string; value: any };

type TProps = {
  placeholder?: string;
  name: string;
  btnStyle: React.CSSProperties;
  dropdownStyle: CSSProperties;
  selectList: TDropdownItem[];
  useAll?: boolean;
  disabled?: boolean;
  onClick?: (name: string, selectedItem: TDropdownItem) => void;
  defaultValue?: TDropdownItem;
  required?: boolean;
};

const FormDropdown = ({
  placeholder,
  name,
  btnStyle,
  dropdownStyle,
  selectList,
  useAll = false,
  disabled,
  onClick,
  defaultValue,
  required = true,
}: TProps) => {
  const innerSelectList = useAll
    ? [{ label: '전체', value: null }, ...selectList]
    : selectList;

  const divRef = useRef<HTMLDivElement | null>(null);
  const [showSelectList, setShowSelectList] = useState(false);

  const {
    field: { ref, value: fieldValue, onChange },
  } = useController({ name, rules: { required } });

  const dropdownRef = useCombineRef<HTMLDivElement>(ref, divRef);

  const selectedItem = innerSelectList.find(
    (item) => item.value?.toString() === fieldValue?.toString()
  );

  const onClickBtn = () => {
    setShowSelectList(!showSelectList);
  };

  const handleOptionClick = (item: { label: string; value: string }) => {
    onChange(item.value);
    setShowSelectList(false);
    onClick && onClick(name, item);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(event.target as Node)) {
        setShowSelectList && setShowSelectList(() => false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div className={styles.btn_wrapper} ref={dropdownRef}>
      <button
        type="button"
        className={classNames(styles.select_btn, {
          [styles.active]:
            (selectedItem && selectedItem.label !== '전체') ||
            (defaultValue && defaultValue.label !== '전체'),
        })}
        style={btnStyle}
        onClick={onClickBtn}
        disabled={selectList.length === 0 || disabled}
      >
        <span className={styles.span}>
          {(selectedItem?.label &&
            selectedItem.label !== '전체' &&
            selectedItem?.label) ||
            (defaultValue && defaultValue.label !== '전체'
              ? defaultValue.label
              : placeholder)}
        </span>
        {!showSelectList ? (
          <ChevronDown
            width={24}
            height={24}
            fill={
              selectedItem && selectedItem.label !== '전체'
                ? STYLES_VARIABLES.GRAY_08
                : selectList.length === 0 || disabled
                ? STYLES_VARIABLES.GRAY_04
                : STYLES_VARIABLES.GRAY_07
            }
          />
        ) : (
          <ChevronUp
            width={24}
            height={24}
            fill={
              selectedItem ? STYLES_VARIABLES.GRAY_08 : STYLES_VARIABLES.GRAY_07
            }
          />
        )}
      </button>
      <AnimatePresence>
        {showSelectList && (
          <motion.ul
            className={classNames(styles.option_list, {
              [styles.open]: showSelectList,
            })}
            style={dropdownStyle}
            initial={{ opacity: 0, height: 'auto' }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 'auto' }}
            transition={{ duration: 0.2 }}
          >
            {innerSelectList?.map((item, index) => {
              return (
                <motion.li
                  className={classNames(styles.option_button, {
                    [styles.selected]: item === selectedItem,
                  })}
                  key={index}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => {
                    handleOptionClick(item);
                  }}
                >
                  <span>{item.label}</span>
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FormDropdown;
